/**
 * @file Donne à une ligne de tableau le comportement d'un lien sans en être un.
 * @module packages.common.hooks.useRowLinkNavigation
 *
 * Une ligne de tableau ne peut pas être une ancre. Ce hook reproduit sur toute
 * la surface de la ligne les gestes que l'utilisateur attend d'un lien :
 *   - clic simple : navigation dans l'application, avec un état de navigation optionnel ;
 *   - clic molette, Ctrl/Cmd-clic, Maj-clic : ouverture dans un nouvel onglet ;
 *   - clic sur un lien ou un bouton contenu dans la ligne : laissé au natif.
 *
 * L'ancre réelle (nom du client, par exemple) reste nécessaire dans la ligne
 * pour le clic droit, le focus clavier et les lecteurs d'écran ; ce hook
 * évite justement de doubler sa navigation.
 */

import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';

const MIDDLE_BUTTON = 1;
const NATIVE_INTERACTIVE_SELECTOR = 'a, button, [role="button"], input, select, textarea, label';

/**
 * Indique si l'événement provient d'un élément qui gère lui-même son interaction.
 *
 * @param {Event} event - Événement souris
 * @return {boolean} Vrai si la ligne ne doit pas réagir
 */
function isNativeInteractiveTarget(event) {
  return Boolean(event.target?.closest?.(NATIVE_INTERACTIVE_SELECTOR));
}

/**
 * Indique si le geste demande une ouverture dans un nouvel onglet.
 *
 * @param {MouseEvent} event - Événement souris
 * @return {boolean} Vrai pour Ctrl/Cmd-clic, Maj-clic ou clic molette
 */
function wantsNewTab(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.button === MIDDLE_BUTTON;
}

/**
 * Fournit les gestionnaires souris d'une ligne cliquable.
 *
 * @param {string} href - Destination (chemin interne de l'application)
 * @param {Object} [options] - Options
 * @param {Object} [options.state] - État transmis à la navigation interne (location.state)
 * @return {Object} Gestionnaires {onClick, onAuxClick} à étaler sur la ligne
 */
export function useRowLinkNavigation(href, options = {}) {
  const {state} = options;
  const navigate = useNavigate();

  const openInNewTab = useCallback(() => {
    window.open(href, '_blank', 'noopener');
  }, [href]);

  const onClick = useCallback(
      (event) => {
        if (isNativeInteractiveTarget(event)) return;
        if (wantsNewTab(event)) {
          event.preventDefault();
          openInNewTab();
          return;
        }
        navigate(href, {state});
      },
      [href, state, navigate, openInNewTab],
  );

  const onAuxClick = useCallback(
      (event) => {
        if (event.button !== MIDDLE_BUTTON || isNativeInteractiveTarget(event)) return;
        event.preventDefault();
        openInNewTab();
      },
      [openInNewTab],
  );

  return useMemo(() => ({onClick, onAuxClick}), [onClick, onAuxClick]);
}
